/* oc-cart-rail.js — behaviour for snippets/oc-complete-the-fit.liquid.

   MUST live in an asset, never inline in the snippet. The rail only renders when
   there is an offer to show, and Horizon repaints the drawer through
   `new DOMParser().parseFromString(...)` (assets/morph.js). DOMParser-created
   <script> elements are flagged already-started by the HTML spec and never run,
   and morph.js only re-creates `.shopify-app-block script[src]` — which we are
   not. Worse, component-cart-items.js runs the drawer in `hydration` mode, and
   morph.js returns from that branch before it even reaches that escape hatch.

   So an inline script was 100% inert on the PRIMARY path: land with an empty
   cart -> add an item -> drawer repaints with the rail -> nothing bound.
   Found by the W7 review. The earlier "verified" only proved the server-rendered
   MARKUP was right, which says nothing about whether the JS ever executes.

   Loaded from BOTH surfaces, because they are gated differently:
     - snippets/header-actions.liquid — the drawer, gated on
       `cart_type == 'drawer' and template.name != 'cart'`
     - blocks/oc-cart-upsell.liquid   — the /cart page, which that gate EXCLUDES
   The __ocFitBound latch below makes a double load harmless.

   Every listener is delegated on document, so one load survives every repaint.
   Nothing here may hold a reference to an element across a repaint — morph
   replaces nodes, so state lives in the maps below, keyed by product id.
*/
(function () {
  if (window.__ocFitBound) return;
  window.__ocFitBound = true;

  // Survives repaints because it is NOT in the DOM. See restore() below.
  var picked = {};    // productId -> variantId the shopper actually chose
  var inFlight = {};  // productId -> true while an add is posting

  function pidOf(form) { return form.getAttribute('data-oc-fit-product'); }

  function formFor(pid) {
    return document.querySelector('[data-oc-fit-form][data-oc-fit-product="' + pid + '"]');
  }

  /* SPEED: one round trip instead of three.
     This used to POST /cart/add.js, then separately GET /cart.js, then dispatch —
     and because the event carried no rendered HTML, Horizon's cart component had
     to go fetch the section itself. Three sequential round trips before anything
     moved on screen, which is why adding felt sluggish.

     Horizon's own product-form.js does it in ONE: it appends `sections` (collected
     from every cart-items-component's data-section-id) and `sections_url` to the
     add request, so Shopify returns the rendered section HTML in the SAME response,
     then passes response.sections through on the event. Mirroring that exactly. */
  function sectionIds() {
    var ids = [];
    var nodes = document.querySelectorAll('cart-items-component[data-section-id]');
    for (var i = 0; i < nodes.length; i++) {
      var id = nodes[i].getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    }
    return ids;
  }

  // Horizon reads the new count out of the returned section markup rather than
  // making another cart request (component-cart-items.js does the same with
  // [ref="cartItemCount"]). Same trick here — no extra round trip for a number.
  function countFromSections(sections) {
    if (!sections) return undefined;
    for (var k in sections) {
      if (!Object.prototype.hasOwnProperty.call(sections, k)) continue;
      try {
        var doc = new DOMParser().parseFromString(sections[k], 'text/html');
        var el = doc.querySelector('[ref="cartItemCount"]');
        if (el && el.textContent) {
          var n = parseInt(el.textContent.trim(), 10);
          if (!isNaN(n)) return n;
        }
      } catch (e) { /* malformed section: fall through, count is advisory */ }
    }
    return undefined;
  }

  function addUrl() {
    // Locale/market aware when Horizon exposes it; '/cart/add.js' is only a last resort.
    //
    // Horizon publishes this route ALREADY SUFFIXED — Theme.routes.cart_add_url is
    // literally '/cart/add.js', not '/cart/add'. Appending '.js' unconditionally
    // produced '/cart/add.js.js', which 404s. The 404 then fell into this file's own
    // server-rejection branch and surfaced as "Could not add that size." with no
    // console error, so the button looked silently dead. Every add was broken.
    // Only the `base` branch was affected; the fallback string was always right,
    // which is why reading the line did not reveal it.
    var base = window.Theme && Theme.routes && Theme.routes.cart_add_url;
    if (!base) return '/cart/add.js';
    return /\.js$/.test(base) ? base : base + '.js';
  }

  function message(form, text) {
    var el = form.querySelector('[data-oc-fit-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'oc-fit__error';
      el.setAttribute('data-oc-fit-error', '');
      el.setAttribute('role', 'alert');
      form.appendChild(el);
    }
    el.textContent = text;
    el.hidden = !text;
  }

  // The price shown is the SELECTED variant's, not the product's minimum, so it has
  // to repaint when the size changes. Values come from data-* on each radio.
  function paintPrice(form, radio) {
    var box = form.closest('.oc-fit');
    if (!box || !radio) return;
    var price = box.querySelector('[data-oc-fit-price]');
    var cmp = box.querySelector('[data-oc-fit-compare]');
    if (price && radio.getAttribute('data-price')) {
      price.textContent = radio.getAttribute('data-price');
    }
    if (cmp) {
      var c = radio.getAttribute('data-compare') || '';
      cmp.textContent = c;
      cmp.hidden = !c;
    }
  }

  /* THE WRONG-SIZE BUG. Horizon re-renders the drawer from the server on every cart
     mutation, and the server always ships the DEFAULT variant checked — the section
     render carries no ?variant=. morph.js then reverts our hidden input
     (updateInput at :386) and re-checks the default (updateAttribute 'checked' at
     :297), both OUTSIDE its isEqualNode guard, and our fieldset is not in Horizon's
     onBeforeUpdate preserve-list.

     Net effect before this fix: shopper taps 2XL, then nudges the quantity stepper
     on another line, and the chips silently snap back to XS. They press Add and
     receive XS. No error, nothing visible.

     Writing the `checked`/`value` ATTRIBUTES in the change handler does NOT help —
     morph compares live properties and clobbers regardless. The only durable place
     for this is outside the DOM, re-applied after each repaint. */
  function restore(form) {
    var want = picked[pidOf(form)];
    if (!want) return;
    var radio = form.querySelector('.oc-fit__sizes input[value="' + want + '"]');
    // Sold out since they picked it: leave the server's default rather than
    // re-select something that can no longer be bought.
    if (!radio || radio.disabled) return;
    radio.checked = true;
    var hidden = form.querySelector('[data-oc-fit-id]');
    if (hidden) hidden.value = want;
    paintPrice(form, radio);
  }

  document.addEventListener('cart:update', function () {
    // Deferred one task ON PURPOSE. component-cart-items.js dispatches cart:update
    // at :187 and morphs at :195 — re-applying on this tick would be overwritten by
    // the morph that has not happened yet.
    setTimeout(function () {
      var forms = document.querySelectorAll('[data-oc-fit-form]');
      for (var i = 0; i < forms.length; i++) restore(forms[i]);
    }, 0);
  });

  // Size radios drive the hidden variant id. This is also why the native POST
  // fallback cannot be trusted for a multi-size product: the radio's name is
  // `oc-fit-size-<id>`, a param /cart/add ignores, so a plain form submit would
  // silently add `chosen` (the default variant) no matter which size was picked.
  document.addEventListener('change', function (e) {
    var input = e.target.closest('.oc-fit__sizes input');
    if (!input) return;
    var form = input.closest('[data-oc-fit-form]');
    if (!form) return;
    picked[pidOf(form)] = input.value;
    form.querySelector('[data-oc-fit-id]').value = input.value;
    paintPrice(form, input);
    message(form, '');
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-oc-fit-form]');
    if (!form) return;
    e.preventDefault();

    // Double-submit guard keyed on the PRODUCT, not on the button element. A repaint
    // mid-flight replaces the button, which would wipe an element-held aria-busy and
    // re-arm the control for a second POST — i.e. charge the shopper twice.
    var pid = pidOf(form);
    if (inFlight[pid]) return;
    inFlight[pid] = true;

    var btn = form.querySelector('[data-oc-fit-submit]');
    if (btn) {
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;      // aria-busy alone is advisory; this actually blocks
    }
    message(form, '');

    function done() {
      delete inFlight[pid];
      // Re-query: the element captured above may have been replaced by a repaint.
      var f = formFor(pid);
      var b = f && f.querySelector('[data-oc-fit-submit]');
      if (b) {
        b.removeAttribute('aria-busy');
        b.disabled = false;
      }
    }

    fetch(addUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(form.querySelector('[data-oc-fit-id]').value), quantity: 1 }],
        // Section Rendering API: Shopify returns the re-rendered cart markup in
        // THIS response, so there is no second request for the cart and no third
        // for the section. sections_url must be the current path or Shopify
        // renders the sections against the wrong template.
        sections: sectionIds().join(','),
        sections_url: window.location.pathname
      })
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          // SERVER REJECTION (422 sold out / invalid variant). Distinct from a
          // transport failure: re-POSTing the identical payload natively would
          // fail identically and dump the shopper on an unbranded error page.
          if (!r.ok) {
            var err = new Error(j.description || j.message || 'Could not add that size.');
            err.serverRejected = true;
            throw err;
          }
          return j;
        });
      })
      .then(function (res) {
        // The add ALREADY SUCCEEDED past this point. Any failure from here is a
        // refresh problem, never a reason to re-submit — doing so would add the
        // item a SECOND time and charge the shopper twice.
        delete picked[pid];   // this offer is about to retire; drop its state

        // Clear busy BEFORE the paint that consumes it, or the control repaints
        // greyed-out and dead with nothing scheduled to undo it.
        done();

        // `source` MUST differ from 'product-form-component'. Horizon reuses ONE
        // event name ('cart:update') for adds and updates and de-dupes on source;
        // reusing theirs would make this look like a PDP add and loop.
        // `sections` is passed through so listeners morph from markup they already
        // have — this is what makes the update feel immediate instead of waiting
        // on another network round trip.
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: res,
            sourceId: 'oc-fit',
            data: {
              source: 'oc-complete-the-fit',
              productId: pid,
              itemCount: countFromSections(res && res.sections),
              sections: res && res.sections
            }
          }
        }));
      })
      .catch(function (err) {
        done();
        if (err && err.serverRejected) {
          message(form, err.message);   // stay in the drawer, tell them why
          return;
        }
        // Genuine transport failure and nothing was added — the native POST is a
        // real second chance. Only safe for a single-variant match; for a sized
        // product it would add the default size, so surface the error instead.
        if (form.querySelector('.oc-fit__sizes')) {
          message(form, 'Network problem — please try again.');
          return;
        }
        form.submit();
      });
  });
})();
